import { Link } from 'react-router-dom';
import { ASSETS, PATH_SUMMARIES, FACILITATORS, CLOSING_STATEMENT, BRAND } from '../lib/content';
import { Sprout, Leaf, Sparkles, User, Users, Home as HomeIcon } from 'lucide-react';
import Newsletter from '../components/Newsletter';

const FAMILIAR = [
  'Food occupies your thoughts from the moment you wake until you go to sleep.',
  'You feel trapped in cycles of restriction, binge eating, emotional eating or constant dieting.',
  'Every meal brings guilt, anxiety or self-criticism.',
  'You feel disconnected from your body.',
  'You feel ashamed of your eating or your appearance.',
  'You avoid social occasions because food feels overwhelming.',
  'Relationships have become more difficult.',
  'You feel isolated or misunderstood.',
  'Work or university feels increasingly difficult because your energy and concentration have disappeared.',
  'You are exhausted by the constant mental battle.',
  'You no longer recognise yourself.',
  'You worry that things may never change.',
];

const PATHWAYS = [
  {
    icon: Users,
    title: 'Therapeutic Pathways',
    body: 'Small therapeutic groups following our year-long developmental programme.',
    cta: 'Begin with Path I',
    to: '/courses',
  },
  {
    icon: User,
    title: 'One-to-One Therapy',
    body: 'Individual psychotherapy for those who prefer private therapeutic work or would like support alongside the programme.',
    cta: 'Explore individual support',
    to: '/therapy',
  },
  {
    icon: HomeIcon,
    title: 'Family & Couples Support',
    body: 'Support for parents, partners and families navigating eating disorder recovery together.',
    cta: 'Learn more',
    to: '/therapy',
  },
];

const JOURNAL_RECEIVES = [
  'Seasonal reflections',
  'Therapeutic insights',
  'Poetry',
  'Journal prompts',
  'Embodiment practices',
  'News about future pathways, workshops and retreats',
];

const PATH_ICONS = { 'path-1': Sprout, 'path-2': Leaf, 'path-3': Sparkles };

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO — the meeting */}
      <section className="relative overflow-hidden">
        <div className="relative min-h-[86vh] w-full">
          <img
            src={ASSETS.heroMeadow}
            alt="Soft wildflowers at gentle sunset light"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/45 via-brand-bg/25 to-brand-bg" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
              <div className="max-w-2xl fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-bone/85 backdrop-blur-sm text-[11px] uppercase tracking-[0.22em] text-brand-brown-light mb-8 border border-brand-brown/5">
                  <Leaf size={12} className="text-brand-sage-dark" /> A therapeutic pathway
                </div>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-brand-brown leading-[1.05]">
                  You don&rsquo;t have to <em className="italic font-light text-brand-brown-light">carry this</em> alone.
                </h1>
                <p className="mt-7 font-sans text-lg md:text-xl text-brand-brown-light/95 max-w-xl leading-relaxed">
                  If you&rsquo;re feeling trapped in cycles of food, body image, guilt or self-criticism, there is another way.
                </p>
                <p className="mt-3 font-serif italic text-2xl text-brand-brown-light">
                  Recovery is possible.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/courses"
                    data-testid="hero-cta-begin"
                    className="font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-8 py-3.5 hover:bg-brand-brown-light transition-colors duration-500"
                  >
                    Begin Your Journey
                  </Link>
                  <Link
                    to="/booking"
                    data-testid="hero-cta-conversation"
                    className="font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-8 py-3.5 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500"
                  >
                    Book a Complimentary Conversation
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1 — Does any of this feel familiar? */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <div className="divider-leaf mb-8" />
            <h2 className="font-serif text-3xl md:text-5xl text-brand-brown leading-tight">
              Does any of this feel familiar?
            </h2>
            <p className="mt-6 font-sans text-base text-brand-brown-light/90">Perhaps&hellip;</p>
          </div>

          <ul className="space-y-4 max-w-3xl mx-auto">
            {FAMILIAR.map((line, i) => (
              <li
                key={i}
                data-testid={`familiar-${i}`}
                className="flex items-start gap-4 bg-brand-bone rounded-2xl px-6 py-4 border border-brand-brown/5"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-brand-gold mt-2.5 shrink-0" />
                <span className="font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>

          <p className="mt-12 text-center font-serif italic text-xl md:text-2xl text-brand-brown-light max-w-2xl mx-auto leading-relaxed">
            Or perhaps&hellip; you are watching someone you love struggle and feel unsure how to help.
          </p>

          <div className="mt-16 max-w-2xl mx-auto text-center bg-brand-sage rounded-3xl p-10 md:p-12">
            <p className="font-sans text-sm uppercase tracking-[0.22em] text-brand-brown-muted mb-5">If any of this feels familiar&hellip;</p>
            <p className="font-serif text-2xl md:text-3xl text-brand-brown leading-tight">Please know this.</p>
            <div className="mt-6 space-y-2 font-serif italic text-xl md:text-2xl text-brand-brown-light">
              <p>You are not alone.</p>
              <p>Recovery is possible.</p>
              <p className="text-brand-gold">There is hope.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Healing is about more than food */}
      <section className="py-24 md:py-28 bg-brand-bg-soft">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Our philosophy</p>
            <h2 className="font-serif text-3xl md:text-5xl text-brand-brown leading-tight">
              Healing is about more than food.
            </h2>
          </div>
          <div className="space-y-5 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
            <p>At Nurture the Self, we believe eating disorders and disordered eating are rarely just about food.</p>
            <p>They often arise alongside experiences of perfectionism, overwhelm, anxiety, trauma, loss, relationships, identity, self-worth or life transitions. Food may become the visible expression of something much deeper.</p>
            <p className="font-serif italic text-xl text-brand-brown-light">Our work is not about fixing you.</p>
            <p>It is about gently helping you reconnect with the parts of yourself that may have become hidden beneath years of coping, surviving or striving.</p>
            <p>Through psychotherapy, anthroposophically informed practice, embodied awareness and compassionate therapeutic relationships, we support people in rebuilding trust in themselves, their bodies and their capacity to heal.</p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — More than one path towards healing */}
      <section className="py-24 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">How we work</p>
            <h2 className="font-serif text-3xl md:text-5xl text-brand-brown leading-tight">
              There is more than one path towards healing.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PATHWAYS.map((p, i) => (
              <Link
                key={p.title}
                to={p.to}
                data-testid={`home-pathway-${i}`}
                className="group block bg-brand-bone rounded-3xl p-8 md:p-10 border border-brand-brown/5 hover:border-brand-sage-dark transition-all duration-500"
              >
                <div className="h-12 w-12 rounded-full bg-brand-sage flex items-center justify-center mb-5">
                  <p.icon size={20} className="text-brand-brown" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl text-brand-brown leading-snug mb-3">{p.title}</h3>
                <p className="font-sans text-sm text-brand-brown-light/95 leading-relaxed">{p.body}</p>
                <p className="mt-6 font-sans text-xs uppercase tracking-[0.22em] text-brand-brown group-hover:text-brand-gold transition-colors">
                  {p.cta} →
                </p>
              </Link>
            ))}
          </div>

          <p className="mt-12 text-center font-serif italic text-lg md:text-xl text-brand-brown-light max-w-2xl mx-auto leading-relaxed">
            Different people need different forms of support at different stages of recovery. There is no single &ldquo;right&rdquo; way to heal.
          </p>
        </div>
      </section>

      {/* SECTION 4 — Meet Your Therapists */}
      <section className="py-24 md:py-28 bg-brand-bg-soft">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Two experienced practitioners</p>
            <h2 className="font-serif text-3xl md:text-5xl text-brand-brown leading-tight">Meet your therapists.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {FACILITATORS.map((f, i) => (
              <div key={f.name} data-testid={`home-therapist-${i}`} className="bg-brand-bone rounded-3xl p-8 md:p-10 border border-brand-brown/5">
                <div className="flex items-center gap-6">
                  <img src={f.photo} alt={f.name} className="w-24 h-24 rounded-full object-cover" />
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl text-brand-brown leading-tight">{f.name}</h3>
                    <p className="mt-2 font-sans text-xs uppercase tracking-[0.18em] text-brand-gold leading-relaxed">
                      {f.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 max-w-3xl mx-auto text-center space-y-5 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
            <p>We met whilst training together on the Master&rsquo;s programme in Anthroposophical Psychotherapy and discovered a shared passion for supporting people in recovering relationship with food, body and self.</p>
            <p>Together we created Nurture the Self to offer the kind of compassionate, integrated and deeply human support that we both felt was often missing.</p>
            <Link
              to="/facilitators"
              data-testid="home-meet-cta"
              className="inline-flex mt-2 font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-7 py-3 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500"
            >
              Read our shared vision
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 5 — The Nurture the Self Philosophy */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">The Nurture the Self philosophy</p>
          <h2 className="font-serif text-3xl md:text-5xl text-brand-brown leading-tight">
            Healing is not about changing. It is about returning.
          </h2>
          <div className="mt-10 space-y-3 font-serif italic text-xl md:text-2xl text-brand-brown-light leading-relaxed">
            <p>Relationship with food.</p>
            <p>Relationship with the body.</p>
            <p>Relationship with ourselves.</p>
            <p>Relationship with others.</p>
            <p>Relationship with life.</p>
          </div>
          <p className="mt-10 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
            {CLOSING_STATEMENT.body.split('. ').slice(0, 2).join('. ')}.
          </p>
        </div>
      </section>

      {/* SECTION 6 — Explore Our Therapeutic Pathways (visual journey) */}
      <section className="py-24 md:py-28 bg-brand-sage/40">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">The whole journey</p>
            <h2 className="font-serif text-3xl md:text-5xl text-brand-brown leading-tight">
              Explore our therapeutic pathways.
            </h2>
          </div>

          <div className="space-y-6">
            {PATH_SUMMARIES.map((p, i) => {
              const Icon = PATH_ICONS[p.id] || Leaf;
              return (
                <div key={p.id} data-testid={`home-path-${i + 1}`} className="bg-brand-bone rounded-3xl p-7 md:p-9 border border-brand-brown/5">
                  <div className="flex items-start gap-5">
                    <div className="h-12 w-12 rounded-full bg-brand-sage flex items-center justify-center shrink-0">
                      <Icon size={20} className="text-brand-brown" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold mb-2">{p.label} · {p.stage}</p>
                      <h3 className="font-serif text-2xl md:text-3xl text-brand-brown leading-snug">{p.title}</h3>
                      <p className="mt-3 font-sans text-base text-brand-brown-light/95 leading-relaxed">{p.summary}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-center font-sans text-base text-brand-brown-light/95 italic max-w-2xl mx-auto leading-relaxed">
            Each Path builds gently upon the previous one, supporting sustainable healing over time.
          </p>

          <div className="mt-10 text-center">
            <Link
              to="/courses"
              data-testid="home-explore-paths"
              className="inline-flex font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-8 py-3.5 hover:bg-brand-brown-light transition-colors duration-500"
            >
              Explore Path I
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 7 — Join the community */}
      <section className="py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Newsletter source="home" tone="light" />
          <div className="mt-8 max-w-2xl mx-auto text-center">
            <p className="font-sans text-sm text-brand-brown-muted mb-3">Subscribers to <em className="italic text-brand-brown">The Nurture the Self Journal</em> receive:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {JOURNAL_RECEIVES.map((it) => (
                <span key={it} className="font-sans text-xs px-3 py-1.5 rounded-full bg-brand-bone text-brand-brown-light border border-brand-brown/5">{it}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — Final invitation */}
      <section className="py-24 md:py-32 bg-brand-bg-soft">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="divider-leaf mb-8" />
          <h2 className="font-serif text-3xl md:text-5xl text-brand-brown leading-tight">
            Wherever you are beginning&hellip;
          </h2>
          <div className="mt-8 space-y-5 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
            <p>Whether you are considering joining one of our therapeutic pathways, looking for one-to-one therapy, supporting someone you love or simply exploring what recovery might look like, you are warmly welcome here.</p>
            <p className="font-serif italic text-xl text-brand-brown-light">Healing begins not with perfection, but with the courage to take one gentle step.</p>
            <p>We would be honoured to walk alongside you.</p>
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Link
              to="/courses"
              data-testid="home-final-explore-path"
              className="font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-7 py-3 hover:bg-brand-brown-light transition-colors duration-500"
            >
              Explore Path I
            </Link>
            <Link
              to="/booking"
              data-testid="home-final-conversation"
              className="font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-7 py-3 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500"
            >
              Book a Complimentary Conversation
            </Link>
            <Link
              to="/therapy"
              data-testid="home-final-therapy"
              className="font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-7 py-3 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500"
            >
              Learn About Individual Therapy
            </Link>
          </div>

          <p className="mt-14 font-serif italic text-xl md:text-2xl text-brand-gold tracking-wide">
            {BRAND.strapline}
          </p>
        </div>
      </section>
    </div>
  );
}
